import { nanoid } from "nanoid";
import { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";


import { Button, FormControl, Grid, IconButton, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent, TextField, Checkbox, FormHelperText, Tooltip } from "@mui/material";
import { RemoveCircle } from '@mui/icons-material';


import { Action, Actions, AccessNetwork, WanProfile, TrafficPolicy, DeviceInterface, ActionType, NETWORK_POLICY_TYPE, DEVICE_POLICY_TYPE, DEVICE_TARGET_PER_DEVICE_QUOTA, DEVICE_TARGET_ACCESS_NETWORK, FLOOR_DATA_LAG_MINUTES, FLOOR_MAX_CONSECUTIVE_WINDOWS, formatMinutes } from "../types";
import { DataWithUnitInput } from "../DataWithUnitInput";
import { SERVICE_FUNCTION_TO_FEATURE } from "../../../config";
import { checkLoggedInUserAuthorizedToViewPage, readableBytesFormat } from "../../../utils/util";
import { AddCircle } from "@material-ui/icons";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import _ from "lodash";

interface QuotaActionsProps {
    deviceInterfaces: DeviceInterface[];
    accessNetworks?: AccessNetwork[];
    wanProfiles?: WanProfile[];
    trafficPolicies?: TrafficPolicy[];
    value: Action[];
    readOnly?: boolean;
    forRevert?: boolean;
    notificationOnly?: boolean;
    // false hides the per device form, which only applies to unauthenticated clients
    allowPerDeviceQuota?: boolean;
    // ids of actions the quota already holds: shown as they are, removable, never edited
    lockedActionIds?: Set<string>;
    // the quota's own limit, which caps what a single device may be allowed of it
    quotaLimit?: number;
    // a floor tier fires at or below its percent, after consecutive windows of windowMinutes
    floor?: boolean;
    windowMinutes?: number;
    onChange?: (actions: Action[]) => void;
}

function QuotaActions(props: QuotaActionsProps) {

    const { deviceInterfaces, accessNetworks, wanProfiles, trafficPolicies, value, readOnly, forRevert, notificationOnly, allowPerDeviceQuota = true, lockedActionIds, quotaLimit = 0, floor = false, windowMinutes = 0, onChange } = props;

    // render straight from `value` instead of copying it into state: the copy lagged a render
    // behind, so callbacks could report a stale list back through onChange and two versions
    // ended up overwriting each other in an endless loop
    const quotaActions = value

    const addQuotaAction = () => {
        const newAction: Action = {
            id: nanoid(),
            usage_percentage: 10,
            type: Actions.ActionUndefined,
            executed: false,
            revert: forRevert
        }
        // the mildest useful floor tier; 10 is a ceiling number
        if (floor && !forRevert) {
            newAction.usage_percentage = 75;
            newAction.consecutive_windows = 2;
        }
        if (onChange) onChange([...quotaActions, newAction])
    }

    const deleteQuotaAction = (id: string) => () => {
        if (onChange) onChange(quotaActions.filter(condition => condition.id !== id))
    }

    const changeQuotaAction = (id: string) => (condition: Action) => {
        const index = quotaActions.findIndex(_condition => _condition.id === id)
        // the row is already gone, so there is nothing to report back
        if (index === -1) return
        let _quotaActions = [...quotaActions]
        _quotaActions[index] = condition
        if (onChange) onChange(_quotaActions)
    }

    return (
        <Fragment>
            {quotaActions.map(condition => {
                const isSystemAction = notificationOnly && !(
                    condition.type === Actions.ActionUndefined ||
                    (condition.type === Actions.ActionNotification && condition.usage_percentage !== 100)
                )
                // a stored action is what the quota has already acted on, so it is not
                // rewritten underneath: remove it and add a replacement instead
                const isStored = lockedActionIds?.has(condition.id) ?? false
                const rowReadOnly = readOnly || isSystemAction || isStored
                return <QuotaAction condition={condition} key={condition.id} deviceInterfaces={deviceInterfaces} accessNetworks={accessNetworks} wanProfiles={wanProfiles} trafficPolicies={trafficPolicies}
                    onChange={changeQuotaAction(condition.id)} removeComp={
                        isSystemAction ? <></> :
                        <IconButton aria-label="remove condition" onClick={deleteQuotaAction(condition.id)} disabled={readOnly}>
                            <RemoveCircle />
                        </IconButton>
                    } readOnly={rowReadOnly} locked={isStored && !readOnly} notificationOnly={notificationOnly && !isSystemAction} allowPerDeviceQuota={allowPerDeviceQuota} quotaLimit={quotaLimit} floor={floor} windowMinutes={windowMinutes} />
            })}
            {
                !readOnly && <Button variant="outlined" size="small" startIcon={<AddCircle />} className="quota_management--button_outlined--primary" onClick={addQuotaAction} >
                    ADD ACTION
                </Button>
            }
        </Fragment>
    )
}

interface QuotaActionProps {
    deviceInterfaces: DeviceInterface[];
    condition: Action;
    removeComp: JSX.Element;
    accessNetworks?: AccessNetwork[];
    wanProfiles?: WanProfile[];
    trafficPolicies?: TrafficPolicy[];
    readOnly?: boolean;
    // the action is already saved on the quota, so the row explains how to change it
    locked?: boolean;
    notificationOnly?: boolean;
    allowPerDeviceQuota?: boolean;
    quotaLimit?: number;
    floor?: boolean;
    windowMinutes?: number;
    onChange?: (action: Action) => void;
}

function QuotaAction(props: QuotaActionProps) {

    const { condition, deviceInterfaces, removeComp, accessNetworks, wanProfiles, trafficPolicies, readOnly, locked, notificationOnly, allowPerDeviceQuota = true, quotaLimit = 0, floor = false, windowMinutes = 0, onChange } = props;
    const floorTier = floor && !condition.revert;

    const isAuthorizedToBeta = checkLoggedInUserAuthorizedToViewPage(SERVICE_FUNCTION_TO_FEATURE["beta-feature"]["service"], SERVICE_FUNCTION_TO_FEATURE["beta-feature"]["function"]);

    const [exhaustPercent, setExhaustPercent] = useState<number>(condition.usage_percentage)
    const [actionOnExhaust, setActionOnExhaust] = useState<ActionType>(condition.type)

    const [accessNetworkIds, setAccessNetworkIds] = useState<string[]>(condition.access_networks || [])
    const [wanProfileId, setWanProfileId] = useState<string>(condition.wan_profile_id || '')
    const [trafficPolicyId, setTrafficPolicyId] = useState<string>(condition.traffic_policy_id || '')
    const [interfaces, setInterfaces] = useState<string[]>(condition.interfaces || [])
    const [deviceTarget, setDeviceTarget] = useState<string>(condition.device_target || '')
    const [deviceTargetType, setDeviceTargetType] = useState<string>(condition.device_target_type || DEVICE_TARGET_PER_DEVICE_QUOTA)
    const [deviceQuotaBytes, setDeviceQuotaBytes] = useState<number>(condition.device_quota_bytes || 0)

    // one query returns both kinds of policy; each action offers only its own
    const networkPolicies = useMemo(() => trafficPolicies?.filter(policy => !policy.type || policy.type === NETWORK_POLICY_TYPE) ?? [], [trafficPolicies])
    const devicePolicies = useMemo(() => trafficPolicies?.filter(policy => policy.type === DEVICE_POLICY_TYPE) ?? [], [trafficPolicies])

    const handleChangeExhaustPercent = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = event.target.valueAsNumber;
        if (floorTier) {
            setExhaustPercent(isNaN(val) ? 0 : Math.min(100, Math.max(0, Math.round(val))))
        } else if (val < 1) {
            setExhaustPercent(1)
        } else if (notificationOnly && val >= 100) {
            setExhaustPercent(99)
        } else {
            setExhaustPercent(val)
        }
    }

    const consecutiveWindows = condition.consecutive_windows ?? 1;

    const handleChangeConsecutiveWindows = (event: SelectChangeEvent<number>) => {
        onChange && onChange({ ...condition, consecutive_windows: Number(event.target.value) });
    }

    // P5 caps the choice at 12, but the API takes any count, so a longer saved one still shows
    const windowOptions = useMemo(() => {
        const options = _.range(1, FLOOR_MAX_CONSECUTIVE_WINDOWS + 1);
        return options.includes(consecutiveWindows) ? options : [...options, consecutiveWindows];
    }, [consecutiveWindows])

    const handleChangeActionOnExhaust = (event: SelectChangeEvent) => {
        const _action = event.target.value as ActionType
        setActionOnExhaust(_action)
        if (_action === Actions.ActionAssignDevicePolicy) {
            setTrafficPolicyId("")
            setWanProfileId("")
            setAccessNetworkIds([])
            setInterfaces([])
            setDeviceTargetType(allowPerDeviceQuota ? DEVICE_TARGET_PER_DEVICE_QUOTA : DEVICE_TARGET_ACCESS_NETWORK)
            setDeviceTarget("")
        }
        if (_action === Actions.ActionRemoveDevicePolicy) {
            setTrafficPolicyId("")
            setWanProfileId("")
            setAccessNetworkIds([])
            setInterfaces([])
            setDeviceQuotaBytes(0)
            setDeviceTargetType(allowPerDeviceQuota ? DEVICE_TARGET_PER_DEVICE_QUOTA : DEVICE_TARGET_ACCESS_NETWORK)
            setDeviceTarget("")
        }
        if (_action === Actions.ActionAssignWanProfile || _action === Actions.ActionAssignTrafficPolicy) {
            setWanProfileId("")
            setTrafficPolicyId("")
            setAccessNetworkIds([])
        }
        if (_action === Actions.ActionDisableInterface) {
            setInterfaces([])
        }
    }

    const handleChangeAccessNetworkIds = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        let _value = typeof value === 'string' ? value.split(',') : value
        _value = _value.filter(id => accessNetworks?.find(network => network.id === id))
        setAccessNetworkIds(_value);
    }

    const handleChangeWanProfileId = (event: SelectChangeEvent) => {
        setWanProfileId(event.target.value as string)
    }

    const handleChangeTrafficPolicyId = (event: SelectChangeEvent) => {
        setTrafficPolicyId(event.target.value as string)
    }

    const handleChangeDeviceTargetType = (event: SelectChangeEvent) => {
        const value = event.target.value as string
        setDeviceTargetType(value)
        if (value === DEVICE_TARGET_ACCESS_NETWORK) {
            setDeviceQuotaBytes(0)
        } else {
            setAccessNetworkIds([])
        }
    }

    // the device policy goes on one network, so this holds a single-element list
    const handleChangeDevicePolicyAccessNetwork = (event: SelectChangeEvent) => {
        const value = event.target.value as string
        setAccessNetworkIds(value === '' ? [] : [value])
    }

    const handleChangeDeviceQuota = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDeviceQuotaBytes(Number(event.target.value))
    }

    const handleChangeInterfaces = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        setInterfaces(typeof value === 'string' ? value.split(',') : value);
    }

    const invalidWanProfile = useMemo(() => {
        if (actionOnExhaust === Actions.ActionAssignWanProfile) {
            return !wanProfiles?.find(profile => profile.id === wanProfileId)
        }
        return false
    }, [actionOnExhaust, wanProfileId, wanProfiles])

    const invalidTrafficPolicy = useMemo(() => {
        if (actionOnExhaust === Actions.ActionAssignTrafficPolicy) {
            return !networkPolicies.find(policy => policy.id === trafficPolicyId)
        }
        if (actionOnExhaust === Actions.ActionAssignDevicePolicy) {
            return !devicePolicies.find(policy => policy.id === trafficPolicyId)
        }
        return false
    }, [actionOnExhaust, trafficPolicyId, networkPolicies, devicePolicies])

    // The action fires once the quota reaches its threshold, so the whole network has
    // moved that much and no single client can have moved more. An allowance above it
    // could never be met by anyone, and the action would find nobody to act on.
    const maxDeviceQuotaBytes = useMemo(
        () => Math.floor((quotaLimit * exhaustPercent) / 100),
        [quotaLimit, exhaustPercent]
    )

    // to an access network it needs networks; to a device it needs an allowance, and the
    // clients themselves are found when it fires
    const invalidDeviceTarget = useMemo(() => {
        if (actionOnExhaust !== Actions.ActionAssignDevicePolicy) return false
        if (deviceTargetType === DEVICE_TARGET_ACCESS_NETWORK) return accessNetworkIds.length === 0
        if (deviceQuotaBytes <= 0) return true
        return maxDeviceQuotaBytes > 0 && deviceQuotaBytes > maxDeviceQuotaBytes
    }, [actionOnExhaust, deviceTargetType, accessNetworkIds, deviceQuotaBytes, maxDeviceQuotaBytes])

    const invalidAccessNetworks = useMemo(() => {
        if (actionOnExhaust === Actions.ActionAssignWanProfile || actionOnExhaust === Actions.ActionAssignTrafficPolicy) {
            return accessNetworkIds.filter(id => !accessNetworks?.find(network => network.id === id))
        }
        return []
    }, [actionOnExhaust, accessNetworkIds, accessNetworks])

    const invalidInterfaces = useMemo(() => {
        if (actionOnExhaust === Actions.ActionDisableInterface || actionOnExhaust === Actions.ActionEnableInterfaces) {
            return interfaces.filter(alias => !deviceInterfaces?.find(inf => inf.alias === alias))
        }
        return []
    }, [actionOnExhaust, interfaces, deviceInterfaces])

    useEffect(() => {
        !readOnly && onChange && onChange({
            ...condition,
            usage_percentage: exhaustPercent,
            type: actionOnExhaust,
            access_networks: accessNetworkIds,
            wan_profile_id: wanProfileId,
            traffic_policy_id: trafficPolicyId,
            interfaces: interfaces,
            device_target: deviceTarget,
            device_target_type: deviceTargetType,
            device_quota_bytes: deviceQuotaBytes,
            executed: condition.executed || false,
            valid:
                actionOnExhaust !== Actions.ActionUndefined &&
                !invalidWanProfile &&
                !invalidTrafficPolicy &&
                invalidAccessNetworks.length === 0 &&
                invalidInterfaces.length === 0 &&
                (
                    (
                        actionOnExhaust === Actions.ActionAssignWanProfile &&
                        wanProfileId !== "" && accessNetworkIds.length !== 0
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionAssignTrafficPolicy &&
                        trafficPolicyId !== "" && accessNetworkIds.length !== 0
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionDisableInterface &&
                        interfaces.length !== 0
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionEnableInterfaces &&
                        interfaces.length !== 0
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionNotification
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionPredictOverageNotification
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionResetDevice
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionAssignDevicePolicy &&
                        trafficPolicyId !== "" && !invalidDeviceTarget
                    ) ||
                    (
                        actionOnExhaust === Actions.ActionRemoveDevicePolicy &&
                        (deviceTargetType !== DEVICE_TARGET_ACCESS_NETWORK || accessNetworkIds.length !== 0)
                    )
                )
        })
    }, [exhaustPercent, actionOnExhaust, accessNetworkIds, wanProfileId, trafficPolicyId, interfaces, deviceTarget, deviceTargetType, deviceQuotaBytes, invalidWanProfile, invalidTrafficPolicy, invalidAccessNetworks, invalidInterfaces, invalidDeviceTarget])

    useEffect(() => {
        if (actionOnExhaust === Actions.ActionNotification || actionOnExhaust === Actions.ActionResetDevice) {
            setAccessNetworkIds([])
            setWanProfileId('')
            setTrafficPolicyId('')
            setInterfaces([])
        }
        // Neither device policy action names a client, so a target stored by an older
        // action is dropped and its type normalised rather than left as something
        // nothing can edit.
        if (actionOnExhaust === Actions.ActionAssignDevicePolicy || actionOnExhaust === Actions.ActionRemoveDevicePolicy) {
            if (deviceTargetType !== DEVICE_TARGET_PER_DEVICE_QUOTA && deviceTargetType !== DEVICE_TARGET_ACCESS_NETWORK) {
                setDeviceTargetType(allowPerDeviceQuota ? DEVICE_TARGET_PER_DEVICE_QUOTA : DEVICE_TARGET_ACCESS_NETWORK)
            } else if (!allowPerDeviceQuota && deviceTargetType === DEVICE_TARGET_PER_DEVICE_QUOTA) {
                // the usage type changed under a per device action: only the network form applies
                setDeviceTargetType(DEVICE_TARGET_ACCESS_NETWORK)
                setDeviceQuotaBytes(0)
            }
        }
        if (deviceTarget !== '') {
            setDeviceTarget('')
        }
        if (actionOnExhaust === Actions.ActionAssignWanProfile || actionOnExhaust === Actions.ActionAssignTrafficPolicy) {
            setWanProfileId(condition.wan_profile_id || "")
            setTrafficPolicyId(condition.traffic_policy_id || "")
            setAccessNetworkIds(condition.access_networks || [])
            setInterfaces([])
        }
        if (actionOnExhaust === Actions.ActionDisableInterface) {
            setInterfaces(condition.interfaces || [])
            setWanProfileId('')
            setTrafficPolicyId('')
            setAccessNetworkIds([])
        }
    }, [actionOnExhaust])

    return (
        <Grid classes={{ root: 'quota_management--base--margin_1x' }}>
            <Grid container spacing={0.5}>
                <Grid item xs={12} md={condition.revert ? 3 : floorTier ? 5 : 4}>
                    <Grid container spacing={0.5}>
                        {
                            !condition.revert && <Grid item xs={12} md={floorTier ? 3 : 4}>
                                <FormControl fullWidth>
                                    <TextField
                                        size="small"
                                        label={floorTier ? "≤ % of min" : "% used"}
                                        variant="outlined"
                                        value={exhaustPercent}
                                        type="number"
                                        onChange={handleChangeExhaustPercent}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: floorTier ? 0 : 1, max: floorTier ? 100 : notificationOnly ? 99 : undefined }}
                                        disabled={readOnly}
                                    />
                                    {floorTier && <FormHelperText>{exhaustPercent === 0 ? 'no traffic at all' : `≤ ${readableBytesFormat(Math.floor(quotaLimit * exhaustPercent / 100))}`}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        }
                        {
                            floorTier && <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="consecutive-windows-label">For at least</InputLabel>
                                    <Select
                                        labelId="consecutive-windows-label"
                                        label="For at least"
                                        value={consecutiveWindows}
                                        onChange={handleChangeConsecutiveWindows}
                                        disabled={readOnly}
                                    >
                                        {windowOptions.map(n => <MenuItem key={n} value={n}>{windowMinutes > 0 ? formatMinutes(n * windowMinutes) : `${n} window${n > 1 ? 's' : ''}`}</MenuItem>)}
                                    </Select>
                                    {/* the window is judged once its data settles, so acting trails the drop by the lag too */}
                                    {windowMinutes > 0 && <FormHelperText>{consecutiveWindows} window{consecutiveWindows > 1 ? 's' : ''} in a row · acts ~{formatMinutes(FLOOR_DATA_LAG_MINUTES + consecutiveWindows * windowMinutes)} after traffic drops</FormHelperText>}
                                </FormControl>
                            </Grid>
                        }
                        <Grid item xs={12} md={condition.revert ? 12 : floorTier ? 5 : 8}>
                            <FormControl fullWidth size="small" error={!readOnly && !notificationOnly && ((_.isEmpty(deviceInterfaces) && _.isEmpty(wanProfiles) && _.isEmpty(trafficPolicies)) || actionOnExhaust === Actions.ActionUndefined)}>
                                <InputLabel id="action-on-exhaust-label">Action</InputLabel>
                                <Select
                                    value={actionOnExhaust}
                                    onChange={handleChangeActionOnExhaust}
                                    labelId="action-on-exhaust-label"
                                    label="Action"
                                    disabled={readOnly}
                                >
                                    {!notificationOnly && (readOnly || !_.isEmpty(wanProfiles)) && <MenuItem value={Actions.ActionAssignWanProfile}>Assign WAN Profile</MenuItem>}
                                    {!notificationOnly && (readOnly || !_.isEmpty(trafficPolicies)) && <MenuItem value={Actions.ActionAssignTrafficPolicy}>Assign Network Policy</MenuItem>}
                                    {!notificationOnly && (readOnly || !_.isEmpty(devicePolicies)) && (isAuthorizedToBeta || actionOnExhaust === Actions.ActionAssignDevicePolicy) && <MenuItem value={Actions.ActionAssignDevicePolicy}>Assign Device Policy</MenuItem>}
                                    {/* a floor can't remove a device policy (site-controller 7613cb65); a saved one shows disabled */}
                                    {!notificationOnly && condition.revert && (isAuthorizedToBeta || actionOnExhaust === Actions.ActionRemoveDevicePolicy) && (!floor || actionOnExhaust === Actions.ActionRemoveDevicePolicy) && <MenuItem value={Actions.ActionRemoveDevicePolicy} disabled={floor}>Remove Device Policy</MenuItem>}
                                    {!condition.revert && <MenuItem value={Actions.ActionNotification}>Send Notification</MenuItem>}
                                    {!condition.revert && !notificationOnly && (!floor || actionOnExhaust === Actions.ActionPredictOverageNotification) && <MenuItem value={Actions.ActionPredictOverageNotification} disabled={floor}>Predict Overage Notification</MenuItem>}
                                    {
                                        (readOnly) && actionOnExhaust && !(Object.values(Actions) as string[]).includes(actionOnExhaust) ? <MenuItem value={actionOnExhaust}>{actionOnExhaust.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</MenuItem> : null
                                    }
                                    {/* SPK-239: a floor watches the one link these would switch off, so it isn't
                                        offered; a floor saved with one still shows it, disabled */}
                                    {!notificationOnly && (!floor || actionOnExhaust === Actions.ActionEnableInterfaces || actionOnExhaust === Actions.ActionDisableInterface) && ((readOnly || !_.isEmpty(deviceInterfaces)) ? condition.revert ? <MenuItem value={Actions.ActionEnableInterfaces} disabled={floor}>Enable Interfaces</MenuItem> : <MenuItem value={Actions.ActionDisableInterface} disabled={floor}>Disable Interfaces</MenuItem> : null)}
                                    {!notificationOnly && !condition.revert && (isAuthorizedToBeta || actionOnExhaust === Actions.ActionResetDevice) && <MenuItem value={Actions.ActionResetDevice}>Reset Device</MenuItem>}
                                </Select>
                                {
                                    !readOnly && !notificationOnly && _.isEmpty(deviceInterfaces) && _.isEmpty(wanProfiles) && _.isEmpty(trafficPolicies) ? <FormHelperText>Device data not available</FormHelperText> :
                                        !readOnly && !notificationOnly && actionOnExhaust === Actions.ActionUndefined ? <FormHelperText>Action is required</FormHelperText> :
                                            actionOnExhaust === Actions.ActionRemoveDevicePolicy ? <FormHelperText>{deviceTargetType === DEVICE_TARGET_ACCESS_NETWORK ? 'Removes the device policy from the access networks this quota assigned it to' : 'Removes the device policy from every device this quota assigned it to'}</FormHelperText> : null
                                }
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
                {
                    (actionOnExhaust === Actions.ActionAssignWanProfile || actionOnExhaust === Actions.ActionAssignTrafficPolicy) ? <Grid item xs={12} md={7} sx={readOnly ? { cursor: 'not-allowed', '& input': { cursor: 'not-allowed' }, '& .MuiSelect-select': { cursor: 'not-allowed' } } : {}}>
                        <Grid container spacing={0.5}>
                            {
                                actionOnExhaust === Actions.ActionAssignWanProfile && <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small" error={invalidWanProfile}>
                                        <InputLabel id="wan-profile-label">WAN Profile</InputLabel>
                                        <Select
                                            value={wanProfileId}
                                            onChange={handleChangeWanProfileId}
                                            labelId="wan-profile-label"
                                            label="WAN Profile"
                                            readOnly={readOnly}
                                        >
                                            {wanProfiles && wanProfiles.map(profile => <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>)}
                                        </Select>
                                        {
                                            invalidWanProfile ? wanProfileId === "" ? <FormHelperText> WAN profile is required</FormHelperText> : <FormHelperText>Selected wan profile does not exist, please reselect the wan profile</FormHelperText> : null
                                        }
                                    </FormControl>
                                </Grid>
                            }
                            {
                                actionOnExhaust === Actions.ActionAssignTrafficPolicy && <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small" error={invalidTrafficPolicy}>
                                        <InputLabel id="traffic-policy-label">Network Policy</InputLabel>
                                        <Select
                                            value={trafficPolicyId}
                                            onChange={handleChangeTrafficPolicyId}
                                            labelId="traffic-policy-label"
                                            label="Network Policy"
                                            readOnly={readOnly}
                                        >
                                            {networkPolicies.map(policy => <MenuItem key={policy.id} value={policy.id}>{policy.name}</MenuItem>)}
                                        </Select>
                                        {
                                            invalidTrafficPolicy ? trafficPolicyId === "" ? <FormHelperText>Traffic policy is required</FormHelperText> : <FormHelperText>Selected traffic policy does not exist, please reselect the traffic policy</FormHelperText> : null
                                        }
                                    </FormControl>
                                </Grid>
                            }
                            {
                                (actionOnExhaust === Actions.ActionAssignWanProfile || actionOnExhaust === Actions.ActionAssignTrafficPolicy) && <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small" error={invalidAccessNetworks?.length > 0 || accessNetworkIds?.length === 0}>
                                        <InputLabel id="access-network-label">Access Networks</InputLabel>
                                        <Select
                                            multiple
                                            value={accessNetworkIds}
                                            onChange={handleChangeAccessNetworkIds}
                                            labelId="access-network-label"
                                            label="Access Networks"
                                            renderValue={(selected) => {
                                                let _anName = new Map(accessNetworks?.map(an => [an.id, an.name]))
                                                return (selected as string[]).map(id => _anName.get(id) || '<Deleted>').join(', ')
                                            }}
                                            readOnly={readOnly}
                                        >
                                            {accessNetworks && accessNetworks.map(network => <MenuItem key={network.id} value={network.id}>
                                                <Checkbox size='small' checked={accessNetworkIds.indexOf(network.id) > -1} />
                                                <ListItemText primary={network.name} />
                                            </MenuItem>)}
                                            {
                                                invalidAccessNetworks?.length > 0 ? invalidAccessNetworks.map(id => <MenuItem key={id} value={id}>
                                                    <Checkbox size='small' checked={accessNetworkIds.indexOf(id) > -1} />
                                                    <ListItemText primary={'<Deleted>'} />
                                                </MenuItem>) : null
                                            }
                                        </Select>
                                        {
                                            invalidAccessNetworks?.length > 0 ? <FormHelperText>Selected access networks do not exist, please reselect the access networks</FormHelperText> : accessNetworkIds.length === 0 ? <FormHelperText>Access networks are required</FormHelperText> : null
                                        }
                                    </FormControl>
                                </Grid>
                            }
                        </Grid>
                    </Grid> : actionOnExhaust === Actions.ActionAssignDevicePolicy ? <Grid item xs={12} md={7} sx={readOnly ? { cursor: 'not-allowed', '& input': { cursor: 'not-allowed' }, '& .MuiSelect-select': { cursor: 'not-allowed' } } : {}}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small" error={invalidTrafficPolicy}>
                                    <InputLabel id="device-policy-label">Device Policy</InputLabel>
                                    <Select
                                        value={trafficPolicyId}
                                        onChange={handleChangeTrafficPolicyId}
                                        labelId="device-policy-label"
                                        label="Device Policy"
                                        readOnly={readOnly}
                                    >
                                        {devicePolicies.map(policy => <MenuItem key={policy.id} value={policy.id}>{policy.name}</MenuItem>)}
                                    </Select>
                                    {
                                        invalidTrafficPolicy ? trafficPolicyId === "" ? <FormHelperText>{condition.revert ? 'Choose the device policy to restore on renewal' : 'Device policy is required'}</FormHelperText> : <FormHelperText>Selected device policy does not exist, please reselect the device policy</FormHelperText> : null
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="device-policy-assign-to-label">Assign To</InputLabel>
                                    <Select
                                        value={deviceTargetType === DEVICE_TARGET_ACCESS_NETWORK || !allowPerDeviceQuota || condition.revert ? DEVICE_TARGET_ACCESS_NETWORK : DEVICE_TARGET_PER_DEVICE_QUOTA}
                                        onChange={handleChangeDeviceTargetType}
                                        labelId="device-policy-assign-to-label"
                                        label="Assign To"
                                        readOnly={readOnly}
                                    >
                                        {allowPerDeviceQuota && !condition.revert && <MenuItem value={DEVICE_TARGET_PER_DEVICE_QUOTA}>To Device</MenuItem>}
                                        <MenuItem value={DEVICE_TARGET_ACCESS_NETWORK}>To Access Network</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {
                                deviceTargetType === DEVICE_TARGET_ACCESS_NETWORK ? <Grid item xs={12} md={12}>
                                    <FormControl fullWidth size="small" error={accessNetworkIds?.length === 0}>
                                        <InputLabel id="device-policy-access-network-label">Access Network</InputLabel>
                                        <Select
                                            value={accessNetworkIds?.[0] ?? ''}
                                            onChange={handleChangeDevicePolicyAccessNetwork}
                                            labelId="device-policy-access-network-label"
                                            label="Access Network"
                                            readOnly={readOnly}
                                        >
                                            {accessNetworks && accessNetworks.map(network => <MenuItem key={network.id} value={network.id}>{network.name}</MenuItem>)}
                                        </Select>
                                        {
                                            accessNetworkIds?.length === 0 ? <FormHelperText>Access network is required</FormHelperText> :
                                                <FormHelperText>Applied to every anonymous clients on this access network</FormHelperText>
                                        }
                                    </FormControl>
                                </Grid> : <Grid item xs={12} md={12}>
                                    <DataWithUnitInput value={deviceQuotaBytes} onChange={handleChangeDeviceQuota} disabled={readOnly} label="Per Device Quota" />
                                    {
                                        invalidDeviceTarget && deviceQuotaBytes <= 0 ? <FormHelperText error>Per device quota is required</FormHelperText> :
                                            invalidDeviceTarget ? <FormHelperText error>Cannot exceed {readableBytesFormat(maxDeviceQuotaBytes)}, the quota at {exhaustPercent}%</FormHelperText> :
                                                <FormHelperText>Applied to every unauthenticated client over this much usage</FormHelperText>
                                    }
                                </Grid>
                            }
                        </Grid>
                    </Grid> : actionOnExhaust === Actions.ActionRemoveDevicePolicy ? (
                        deviceTargetType === DEVICE_TARGET_ACCESS_NETWORK ? <Grid item xs={12} md={7} sx={readOnly ? { cursor: 'not-allowed', '& .MuiSelect-select': { cursor: 'not-allowed' } } : {}}>
                            <FormControl fullWidth size="small" error={accessNetworkIds?.length === 0}>
                                <InputLabel id="remove-device-policy-access-network-label">Access Network</InputLabel>
                                <Select
                                    value={accessNetworkIds?.[0] ?? ''}
                                    onChange={handleChangeDevicePolicyAccessNetwork}
                                    labelId="remove-device-policy-access-network-label"
                                    label="Access Network"
                                    readOnly={readOnly}
                                >
                                    {accessNetworks && accessNetworks.map(network => <MenuItem key={network.id} value={network.id}>{network.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid> : <Fragment />
                    ) : (actionOnExhaust === Actions.ActionDisableInterface || actionOnExhaust === Actions.ActionEnableInterfaces) && !_.isEmpty(deviceInterfaces) && <Grid item xs={12} md={7} sx={readOnly ? { cursor: 'not-allowed', '& input': { cursor: 'not-allowed' }, '& .MuiSelect-select': { cursor: 'not-allowed' } } : {}}>
                        <FormControl fullWidth size="small" error={invalidInterfaces?.length > 0 || interfaces?.length === 0}>
                            <InputLabel id="interface-label">Interfaces</InputLabel>
                            <Select
                                multiple
                                value={interfaces}
                                onChange={handleChangeInterfaces}
                                labelId="interface-label"
                                label="Interfaces"
                                renderValue={(selected) => selected.join(', ')}
                                readOnly={readOnly}
                            >
                                {deviceInterfaces.map(inf => <MenuItem key={inf.alias} value={inf.alias}>
                                    <Checkbox size='small' checked={interfaces.indexOf(inf.alias) > -1} />
                                    <ListItemText primary={inf.alias} />
                                </MenuItem>)}
                                {
                                    invalidInterfaces?.length > 0 ? invalidInterfaces.map(alias => <MenuItem key={alias} value={alias}>
                                        <Checkbox size='small' checked={interfaces.indexOf(alias) > -1} />
                                        <ListItemText primary={`${alias} <Deleted>`} />
                                    </MenuItem>) : null
                                }
                            </Select>
                            {
                                invalidInterfaces?.length > 0 ? <FormHelperText>Selected interfaces ({invalidInterfaces?.join(', ')}) do not exist, please reselect the interfaces</FormHelperText> : interfaces.length === 0 ? <FormHelperText>Interfaces are required</FormHelperText> : null
                            }
                        </FormControl>
                    </Grid>
                }

                <Grid item xs={12} md={1} display={'flex'} gap={0.25} alignItems={'center'}>
                    {
                        condition.executed ? <Tooltip title={"Action executed in current quota period"} placement="top">
                            <CheckCircleIcon color={'success'} sx={{ cursor: 'default' }} />
                        </Tooltip> : null
                    }
                    {
                        locked ? <Tooltip title={"A saved action cannot be changed. Remove it and add a new one instead."} placement="top">
                            <LockOutlinedIcon color={'disabled'} fontSize={'small'} sx={{ cursor: 'default' }} />
                        </Tooltip> : null
                    }
                    {removeComp}
                </Grid>
            </Grid>
        </Grid>
    )
}

const mapStateToProps = (state) => ({
    accessNetworks: state.quotaManager.accessNetworks,
    wanProfiles: state.quotaManager.wanProfiles,
    trafficPolicies: state.quotaManager.trafficPolicies
});

export default connect(mapStateToProps, {})(QuotaActions)